import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { stories } from "@/data/content";
import { Quote } from "lucide-react";

export default function Stories() {
  const { t } = useTranslation();
  const filters = [
    { key: "all", label: t("stories.filterAll"), color: "#111827", match: null },
    { key: "hub", label: t("stories.filterHub"), color: "#3663A8", match: "hub" },
    { key: "entrepreneurship", label: t("stories.filterSocial"), color: "#FF9F1C", match: "entrepreneurship" },
    { key: "employment", label: t("stories.filterConsult"), color: "#567CB6", match: "employment" },
  ];
  const [filter, setFilter] = useState("all");
  const list = useMemo(() => (filter === "all" ? stories : stories.filter((s) => s.arm === filter)), [filter]);

  return (
    <div className="relative">
      <section className="pt-16 pb-10">
        <div className="container-iyaf">
          <p className="eyebrow text-iyaf-blue mb-5">{t("stories.eyebrow")}</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95] max-w-4xl text-balance">
            {t("stories.title")}
          </h1>
          <p className="mt-8 font-editorial text-2xl text-iyaf-ink/75 leading-snug max-w-3xl">
            {t("stories.lead")}
          </p>
        </div>
      </section>

      <section className="py-10 sticky top-16 sm:top-20 z-30 bg-iyaf-paper/90 backdrop-blur-md border-y border-black/5">
        <div className="container-iyaf flex flex-wrap gap-2" data-testid="stories-filters">
          {filters.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${active ? "text-white" : "text-iyaf-ink/70 hover:text-iyaf-ink"}`}
                style={active ? { background: f.color } : { background: "white", border: "1px solid rgba(0,0,0,0.08)" }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="py-16">
        <div className="container-iyaf">
          <AnimatePresence mode="wait">
            <motion.div
              key={filter}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {list.map((s, i) => (
                  <motion.article
                    key={s.slug}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.03 }}
                    className="group"
                    data-testid={`story-card-${s.slug}`}
                  >
                    <div className="relative overflow-hidden rounded-[28px] aspect-[4/5] mb-5 bg-iyaf-paper">
                      <img src={s.image} alt={s.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.armColor }} />
                      <span className="small-caps text-[10px] text-iyaf-ink/55">{t(`storiesDeep.armLabel.${s.arm}`, s.arm)}</span>
                    </div>
                    <h3 className="font-display font-black text-2xl leading-tight">{s.name}</h3>
                    <p className="mt-2 text-iyaf-ink/70 leading-relaxed">{t(`storiesDeep.list.${s.slug}.summary`, s.summary)}</p>
                    {s.quote && (
                      <blockquote className="mt-4 p-4 rounded-2xl bg-iyaf-paper border border-black/5 relative">
                        <Quote size={14} className="absolute top-3 left-3 text-iyaf-ink/30" />
                        <p className="pl-6 italic text-sm text-iyaf-ink/75">&ldquo;{t(`storiesDeep.list.${s.slug}.quote`, s.quote)}&rdquo;</p>
                      </blockquote>
                    )}
                    <p className="mt-3 text-[10px] uppercase tracking-widest font-mono" style={{ color: s.armColor }}>
                      → {t(`storiesDeep.list.${s.slug}.impact`, s.impact)}
                    </p>
                  </motion.article>
                ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
}
